import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API } from "../lib/api";

/**
 * Referral share card shown after a successful booking.
 * - Displays a portrait SVG (fetched from /api/referrals/{code}/share-card.svg)
 *   that customers can save/screenshot & share.
 * - One-tap share buttons for WhatsApp, generic Web-Share, and Copy link.
 *
 * Props:
 *  - code (string, required): the customer's own referral code, e.g. "RIZZ-ABC123"
 *  - name (string, optional): pre-fill on the card
 */
export default function ReferralShareCard({ code, name = "" }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/?ref=${encodeURIComponent(code)}`;
  }, [code]);

  const shareText = useMemo(
    () =>
      `Try RIZ N WASH — doorstep laundry in Gurugram. Use my code ${code} for 15% off your first pickup (up to ₹100). ${shareUrl}`,
    [code, shareUrl]
  );

  const svgUrl = `${API}/referrals/${encodeURIComponent(code)}/share-card.svg${
    name ? `?name=${encodeURIComponent(name.split(" ")[0])}` : ""
  }`;
  const pngUrl = `${API}/referrals/${encodeURIComponent(code)}/share-card.png${
    name ? `?name=${encodeURIComponent(name.split(" ")[0])}` : ""
  }`;
  // Story-sized (1080x1920) variant — for Instagram / Facebook Stories.
  const storyPngUrl = `${API}/referrals/${encodeURIComponent(code)}/share-card.png?variant=story${
    name ? `&name=${encodeURIComponent(name.split(" ")[0])}` : ""
  }`;

  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareNative = async () => {
    // Prefer sharing the PNG file so WhatsApp/Instagram show a preview.
    if (navigator.canShare && navigator.share) {
      try {
        const res = await fetch(pngUrl);
        const blob = await res.blob();
        const file = new File([blob], `rizzwash-${code}.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "RIZ N WASH · 15% off",
            text: shareText,
          });
          return;
        }
      } catch {
        /* fall through to text-only share */
      }
    }
    if (navigator.share) {
      try {
        await navigator.share({ title: "RIZ N WASH · 15% off", text: shareText, url: shareUrl });
        return;
      } catch { /* user cancelled */ }
    }
    copyLink();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Share text copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the code instead");
    }
  };

  const downloadCard = () => {
    const a = document.createElement("a");
    a.href = pngUrl;   // PNG preview-friendly on WhatsApp, Instagram, camera roll
    a.download = `riznwash-${code}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Share the 9:16 Instagram-Story variant. Uses Web Share API with a File payload
  // when supported (mobile Chrome / Safari); otherwise falls back to download.
  const shareStory = async () => {
    try {
      const res = await fetch(storyPngUrl);
      const blob = await res.blob();
      const file = new File([blob], `riznwash-${code}-story.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        await navigator.share({
          files: [file],
          title: "RIZ N WASH · Referral",
          text: shareText,
        });
        return;
      }
      // Fallback: download so the user can post to Story manually.
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `riznwash-${code}-story.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Story card downloaded — upload to your Instagram Story");
    } catch (e) {
      toast.error("Couldn't prepare the Story card. Try Save card instead.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="mt-6 rounded-2xl overflow-hidden border border-rw-ink/10 bg-rw-ink text-white"
      data-testid="referral-share-card"
    >
      <div className="grid md:grid-cols-2">
        {/* PNG preview (same asset the user shares) */}
        <div className="relative bg-rw-ink flex items-center justify-center p-4">
          <div className="w-full max-w-[280px] aspect-[4/5] rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            <img
              src={pngUrl}
              alt={`RIZ N WASH referral card ${code}`}
              className="w-full h-full object-cover"
              data-testid="referral-share-svg"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 md:p-8 flex flex-col justify-center gap-5">
          <div>
            <div className="overline text-rw-blue mb-2">Refer & earn ₹100</div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight">
              Share your code.<br />
              <span className="text-rw-blue italic">Both of you win.</span>
            </h3>
            <p className="mt-3 text-sm text-white/70 max-w-sm">
              Your friend gets 15% off their first pickup (up to ₹100). You earn ₹100 credit for
              every friend who books.
            </p>
          </div>

          <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="overline text-white/50">Your code</div>
              <div className="font-mono text-lg font-semibold truncate" data-testid="share-card-code">
                {code}
              </div>
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(code); toast.success("Code copied"); }}
              className="rounded-full border border-white/20 px-3 py-1.5 text-xs font-semibold hover:bg-white hover:text-rw-ink transition-colors"
              data-testid="share-card-copy-code"
            >
              Copy code
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={shareToWhatsApp}
              className="rounded-full bg-[#25D366] hover:bg-[#1ea855] text-white text-sm font-semibold py-2.5 px-4 flex items-center justify-center gap-2 transition-colors"
              data-testid="share-whatsapp-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M20.52 3.48A11.94 11.94 0 0 0 12.06 0C5.53 0 .22 5.31.22 11.84c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.86 11.86 0 0 0 5.66 1.44h.01c6.53 0 11.84-5.31 11.84-11.84 0-3.17-1.24-6.15-3.39-8.44Zm-8.46 18.2h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.21-3.8 1 1.02-3.7-.24-.38a9.83 9.83 0 0 1-1.51-5.18c0-5.44 4.43-9.86 9.87-9.86 2.63 0 5.11 1.03 6.97 2.89a9.79 9.79 0 0 1 2.88 6.98c0 5.44-4.42 9.87-9.86 9.87Zm5.7-7.4c-.31-.16-1.85-.91-2.13-1.02-.29-.1-.5-.16-.71.16-.21.31-.82 1.02-1 1.23-.19.21-.37.24-.68.08-.31-.16-1.31-.48-2.5-1.54-.92-.82-1.55-1.83-1.73-2.14-.18-.31-.02-.48.13-.63.14-.14.31-.37.47-.55.16-.19.21-.31.31-.52.1-.21.05-.39-.03-.55-.08-.16-.71-1.7-.97-2.34-.26-.62-.52-.53-.71-.54h-.6c-.21 0-.55.08-.84.39-.29.31-1.11 1.09-1.11 2.66 0 1.57 1.13 3.08 1.29 3.29.16.21 2.22 3.39 5.39 4.75 3.17 1.36 3.17.91 3.74.85.58-.06 1.85-.76 2.11-1.49.26-.73.26-1.36.18-1.49-.08-.13-.29-.21-.6-.37Z"/>
              </svg>
              WhatsApp
            </button>
            <button
              onClick={shareNative}
              className="rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold py-2.5 px-4 flex items-center justify-center gap-2 transition-colors"
              data-testid="share-native-btn"
            >
              Share…
            </button>
            <button
              onClick={copyLink}
              className={`rounded-full text-sm font-semibold py-2.5 px-4 border transition-colors ${
                copied ? "bg-rw-blue text-rw-ink border-rw-blue" : "border-white/15 text-white hover:bg-white hover:text-rw-ink"
              }`}
              data-testid="share-copy-btn"
            >
              {copied ? "Copied ✓" : "Copy link"}
            </button>
            <button
              onClick={downloadCard}
              className="rounded-full text-sm font-semibold py-2.5 px-4 border border-white/15 text-white hover:bg-white hover:text-rw-ink transition-colors"
              data-testid="share-download-btn"
            >
              Save card
            </button>
          </div>
          <button
            onClick={shareStory}
            className="mt-1 rounded-full text-sm font-semibold py-3 px-4 flex items-center justify-center gap-2 bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white hover:opacity-95 transition-opacity"
            data-testid="share-story-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
            Share to Instagram Story
          </button>
        </div>
      </div>
    </motion.div>
  );
}
